import { makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SearchDeviceContracts, searchDevices } from '../Async/SearchDevicesAsync';
import { ArrowRightIcon, DeviceIcon } from '../Components/Icons';
import { IconProps } from '../Components/StyledIcon';
import { DeviceListFilters } from '../Devices/DeviceList';
import { useAsync, UseAsyncStatus } from '../Hooks/useAsync';
import { routeForDevices } from '../Routes';
import { useHttpConfig } from '../Shared/HttpRequestExecutor';
import { Nullable } from '../Shared/Types';
import { primary, primary8 } from '../Styles/Colours';
import styles from './DeviceSummary.module.scss';

const numColumns = 4;

const useGridVars = makeStyles<
    Theme,
    {
        items: number;
        columns: number;
    }
>({
    root: props => ({
        '--rows': Math.ceil(props.items / props.columns)
    })
});

const useCellVars = makeStyles<
    Theme,
    {
        index: number;
        columns: number;
    }
>({
    root: props => ({
        '--row': Math.ceil((props.index + 1) / props.columns),
        '--column': (props.index % props.columns) + 1
    })
});

const SummaryTile = (props: {
    index: number;
    icon: React.FC<IconProps>;
    info: string;
    description: string;
    actionLabel: string;
    testId: string;
    iconColor: string;
    iconBackgroundColor: string;
    additionalClasses?: string;
    onClick(): void;
}): React.ReactElement => {
    const cssVars = useCellVars({
        index: props.index,
        columns: numColumns
    });
    return (
        <div className={classNames(styles.tileContainer, props.additionalClasses, cssVars.root)}>
            <div className={styles.infoContainer}>
                <span className={styles.icon}>
                    <props.icon color={props.iconColor} backgroundColour={props.iconBackgroundColor} size={'56'} />
                </span>
                <span className={styles.description}>{props.description}</span>
                <span className={styles.info}>{props.info}</span>
            </div>
            <div className={styles.actionContainer} onClick={props.onClick} data-test-id={props.testId}>
                <span>{props.actionLabel}</span>
                <span className={styles.chevron}>
                    <ArrowRightIcon size={'24'} color={primary} />
                </span>
            </div>
        </div>
    );
};

const SummarySection: React.FC<{
    title: string;
    tableActions?: React.ReactElement;
    isVisible?: boolean;
}> = props => {
    const actionsContent = props.tableActions ?? null;
    const section = classNames(styles.summarySection, {
        [styles.hidden]: props.isVisible !== null ? props.isVisible : false
    });
    return (
        <div className={section}>
            <div className={styles.heading}>{props.title}</div>
            <div className={styles.sectionActions}>{actionsContent}</div>
            <div className={styles.content}>{props.children}</div>
        </div>
    );
};

function SummaryTileAsync(props: {
    index: number;
    title: string;
    filter: SearchDeviceContracts.Request.SearchDevices;
}) {
    const httpConfig = useHttpConfig();
    const history = useHistory();

    const dashboardAsync = useAsync({
        httpConfig,
        getApi: searchDevices
    });

    useEffect(() => {
        dashboardAsync.getApi(props.filter, 151);
    }, []);

    const devicesToReview: Nullable<number> =
        dashboardAsync.status === UseAsyncStatus.Loaded && dashboardAsync.data !== null
            ? dashboardAsync.data.length
            : null;

    if (devicesToReview === null) return null;

    const routeState: DeviceListFilters = {
        devices:
            props.filter.devices && props.filter.devices.length
                ? props.filter.devices.map(it => ({
                      authoringSystem: it.authoringSystem,
                      authoringSystemForeignKey: it.authoringSystemForeignKey
                  }))
                : undefined,
        deviceExpiresWithin: props.filter.deviceExpiresWithin ?? undefined,
        keyExpiresWithin: props.filter.keyExpiresWithin ?? undefined,
        statuses: props.filter.statuses && props.filter.statuses.length ? props.filter.statuses : undefined
    };

    return (
        <SummaryTile
            index={props.index}
            icon={DeviceIcon}
            info={devicesToReview.toString()}
            description={props.title}
            actionLabel={'Review devices'}
            testId={'devices-to-review'}
            iconColor={primary}
            iconBackgroundColor={primary8}
            onClick={() => history.push(routeForDevices, routeState)}
        />
    );
}

const oneMonth = '30:00:00';

export const DeviceSummary = (): JSX.Element => {
    const asyncTiles: {
        filter: SearchDeviceContracts.Request.SearchDevices;
        title: string;
    }[] = [
        {
            filter: {
                devices: [],
                deviceExpiresWithin: oneMonth,
                statuses: [],
                keyExpiresWithin: null
            },
            title: 'Devices expiring'
        },
        {
            filter: {
                devices: [],
                deviceExpiresWithin: null,
                statuses: [],
                keyExpiresWithin: oneMonth
            },
            title: 'Devices key expiring'
        }
    ];

    const cssVars = useGridVars({
        items: asyncTiles.length,
        columns: numColumns
    });

    return (
        <div className={styles.summaryContainer}>
            <SummarySection title='Device summary'>
                <div className={classNames(styles.container, cssVars.root)}>
                    {asyncTiles.map((it, index) => (
                        <SummaryTileAsync index={index} key={index} filter={it.filter} title={it.title} />
                    ))}
                </div>
            </SummarySection>
        </div>
    );
};
